/*
 * Progressive enhancement for the local contact CTA. Opening or closing this
 * dialog makes no network request; form delivery is Shopify's standard form
 * behavior and is deliberately not exercised against the production preview.
 */
(() => {
  if (window.rtaContactDialogInitialized) return;
  window.rtaContactDialogInitialized = true;
  const dialogFor = (id) => {
    const element = document.getElementById(id);
    return typeof HTMLDialogElement !== 'undefined' && element instanceof HTMLDialogElement ? element : null;
  };

  // Native dialog focus restoration may retain :focus-visible after a pointer
  // dismissal. Keep the actual return focus, but not a stale keyboard underline.
  const dialogOpeners = new WeakMap();
  const pointerReturns = new Set();
  let lastInput = 'keyboard';
  const clearPointerReturn = (opener) => {
    opener.removeAttribute('data-rta-dialog-pointer-return');
    pointerReturns.delete(opener);
  };
  document.addEventListener('pointerdown', () => { lastInput = 'pointer'; }, true);
  document.addEventListener('keydown', () => {
    lastInput = 'keyboard';
    pointerReturns.forEach(clearPointerReturn);
  }, true);
  document.addEventListener('focusout', (event) => {
    if (pointerReturns.has(event.target)) clearPointerReturn(event.target);
  });
  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (dialog.open) return;
    const opener = dialogOpeners.get(dialog);
    dialogOpeners.delete(dialog);
    if (lastInput !== 'pointer' || !opener?.matches('.rta-contact-cta__action') || !opener.closest('[data-rta-contact-entry]') || document.activeElement !== opener) return;
    opener.setAttribute('data-rta-dialog-pointer-return', '');
    pointerReturns.add(opener);
  }, true);

  const viewportCleanups = new WeakMap();
  const trackViewport = (dialog) => {
    viewportCleanups.get(dialog)?.();
    const viewport = window.visualViewport;
    const update = () => {
      dialog.style.setProperty('--rta-dialog-viewport-top', `${viewport?.offsetTop || 0}px`);
      dialog.style.setProperty('--rta-dialog-viewport-height', `${viewport?.height || window.innerHeight}px`);
    };
    const cleanup = () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      dialog.removeEventListener('close', onClose);
      dialog.style.removeProperty('--rta-dialog-viewport-top');
      dialog.style.removeProperty('--rta-dialog-viewport-height');
      viewportCleanups.delete(dialog);
    };
    // A queued close event may arrive after the same dialog was reopened.
    const onClose = () => { if (!dialog.open) cleanup(); };
    update();
    viewport?.addEventListener('resize', update, { passive: true });
    viewport?.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    dialog.addEventListener('close', onClose);
    viewportCleanups.set(dialog, cleanup);
  };

  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return false;
    clearPointerReturn(opener);
    dialogOpeners.set(dialog, opener);
    // Give native dismissal a stable return target, including direct URL entry.
    opener.focus({ preventScroll: true });
    trackViewport(dialog);
    dialog.showModal();
    return true;
  };

  let linkedDialog = null;
  const contactEntry = () => {
    const section = document.querySelector('[data-rta-contact-entry]')?.closest('.rta-contact-cta');
    const opener = section?.querySelector('.rta-contact-cta__action[data-rta-dialog-open]');
    if (!opener || opener.disabled || opener.getAttribute('aria-disabled') === 'true') return null;
    const dialog = dialogFor(opener.dataset.rtaDialogOpen);
    return dialog && section.contains(dialog) ? { dialog, opener } : null;
  };
  const syncContactHash = () => {
    const entry = contactEntry();
    const requested = window.location.hash === '#contact' && !window.Shopify?.designMode;
    if (linkedDialog && (!requested || linkedDialog !== entry?.dialog)) {
      const previous = linkedDialog;
      linkedDialog = null;
      if (previous.open) previous.close();
    }
    if (!requested || !entry) return;
    if (entry.dialog.open || openDialog(entry.dialog, entry.opener)) {
      linkedDialog = entry.dialog;
      // Initial fragment navigation can move focus after DOMContentLoaded.
      // Reconcile on pageshow without stealing focus from a field already used.
      if (!entry.dialog.contains(document.activeElement)) {
        entry.dialog.querySelector('[data-rta-dialog-close]')?.focus({ preventScroll: true });
      }
    }
  };

  document.addEventListener('close', (event) => {
    if (event.target !== linkedDialog || linkedDialog.open) return;
    linkedDialog = null;
    if (window.location.hash !== '#contact') return;
    // Dismiss in place; direct arrivals must never be sent back off-site.
    // Preserve locale, query parameters and another component's history state.
    const destination = new URL(window.location.href);
    destination.hash = 'rta-contact';
    window.history.replaceState(window.history.state, '', destination.href);
  }, true);

  window.addEventListener('hashchange', syncContactHash);
  window.addEventListener('pageshow', syncContactHash);
  document.addEventListener('shopify:section:load', syncContactHash);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncContactHash, { once: true });
  } else {
    syncContactHash();
  }

  document.addEventListener('shopify:section:unload', (event) => {
    pointerReturns.forEach((opener) => {
      if (event.target.contains(opener)) clearPointerReturn(opener);
    });
    if (linkedDialog && event.target.contains(linkedDialog)) {
      const previous = linkedDialog;
      linkedDialog = null;
      if (previous.open) previous.close();
    }
    event.target.querySelectorAll('.rta-contact-dialog').forEach((dialog) => {
      viewportCleanups.get(dialog)?.();
      dialogOpeners.delete(dialog);
    });
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest('[data-rta-dialog-open]');
    if (opener) {
      if (opener.disabled || opener.getAttribute('aria-disabled') === 'true') return;
      const dialog = dialogFor(opener.dataset.rtaDialogOpen);
      const message = dialog?.querySelector('textarea[name="contact[body]"]');
      if (message && opener.dataset.rtaEnquiryMessage !== undefined) {
        // Refresh an untouched prefill for the selected variant, keeping a
        // visitor's own draft intact when they close/reopen or change options.
        if (!message.value || message.value === (message.dataset.rtaPrefill ?? message.defaultValue)) {
          message.value = opener.dataset.rtaEnquiryMessage;
          message.dataset.rtaPrefill = message.value;
        }
      }
      openDialog(dialog, opener);
      return;
    }

    const closer = event.target.closest('[data-rta-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    if (typeof HTMLDialogElement !== 'undefined' && event.target instanceof HTMLDialogElement) event.target.close();
  });
})();
